import { MantineProvider } from '@mantine/core';
import { Notifications } from '@mantine/notifications';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import '@mantine/core/styles.css';
import '@mantine/charts/styles.css';
import '@mantine/notifications/styles.css';
import './theme/fonts.css';
import './print.css';
import './i18n';
import { theme } from './theme/theme';
import { router } from './router';
import { ApiError } from './api/client';
import { AuthProvider } from './auth/AuthProvider';
import { useAuth } from './auth/useAuth';
import { PageLoader } from './components/PageLoader';

// networkMode: 'always' avoids queries getting stuck in a "paused" fetchStatus (and their
// callers stuck showing a loader forever) if the browser's online/offline signal is ever wrong
// (flaky extensions, corporate proxies) — every request here already needs the session cookie
// to succeed, so there's no offline-friendly cache-only mode to fall back to anyway.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // A 4xx (forbidden, not found, validation) is a deterministic failure a retry won't fix;
      // only retry once for everything else (a transient network or server error).
      retry: (failureCount, error) => !(error instanceof ApiError && error.status >= 400 && error.status < 500) && failureCount < 1,
      staleTime: 10_000,
      networkMode: 'always',
    },
  },
});

/** Waits for the initial /api/auth/me check before mounting the router, which needs `auth` in its context. */
function RoutedApp() {
  const auth = useAuth();
  if (auth.status === 'loading') return <PageLoader />;
  return <RouterProvider router={router} context={{ auth }} />;
}

export default function App() {
  return (
    <MantineProvider theme={theme} forceColorScheme="light">
      <Notifications position="bottom-right" />
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <RoutedApp />
        </AuthProvider>
      </QueryClientProvider>
    </MantineProvider>
  );
}
